import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CollectionsTable, RecordPaymentForm } from './components';
import { AsyncBoundary } from '../../../components/AsyncBoundary';
import {
  CollectionRow,
  RecordPaymentDetails,
  PaymentType,
  CollectionStatus,
} from './driverCollectionsTypes';
import { useDeliveriesStore } from '../../../store/deliveriesStore';
import { useCashCollectionsStore } from '../../../store/cashCollectionsStore';
import { Invoice } from '../../../services/invoicesApi';
import { formatOrderId } from '../../../utils/formatId';
import styles from './DriverCollections.module.css';

interface OutstandingInvoice {
  invoice: Invoice;
  customerName: string;
  stopId?: number;
}

const DriverCollections: React.FC = () => {
  const navigate = useNavigate();
  const stops = useDeliveriesStore((s) => s.stops);
  const fetchMyStops = useDeliveriesStore((s) => s.fetchMyStops);
  const collections = useCashCollectionsStore((s) => s.items);
  const fetchMine = useCashCollectionsStore((s) => s.fetchMine);
  const record = useCashCollectionsStore((s) => s.record);
  const saving = useCashCollectionsStore((s) => s.saving);

  const [outstanding, setOutstanding] = useState<OutstandingInvoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);

  useEffect(() => {
    fetchMyStops();
    fetchMine();
  }, [fetchMyStops, fetchMine]);

  // Surface today's stops as collection candidates. The amount due is
  // approximated by the order total — true invoice-level joining will
  // be wired when stops carry an explicit invoice_id.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        if (cancelled) return;
        const rows: OutstandingInvoice[] = stops
          .filter((s) => s.status !== 'delivered')
          .map((s) => ({
            invoice: {
              id: -s.id,
              order_id: s.order_id,
              invoice_number: formatOrderId(s.order_id),
              invoice_date: s.created_at,
              subtotal: s.order_total,
              tax_amount: 0,
              total_amount: s.order_total,
              status: 'sent',
              created_at: s.created_at,
              updated_at: s.updated_at,
            } as Invoice,
            customerName: s.customer_name || `Customer #${s.customer_id ?? '—'}`,
            stopId: s.id,
          }));
        setOutstanding(rows);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [stops]);

  // A collection is matched to its order, since we now collect per order
  // (the invoice is found/created server-side at collection time).
  const collectedOrderIds = useMemo(
    () => new Set(collections.map((c) => c.order_id).filter((id): id is number => id != null)),
    [collections]
  );

  const rows = useMemo<CollectionRow[]>(
    () =>
      outstanding.map((o) => {
        const status: CollectionStatus = collectedOrderIds.has(o.invoice.order_id) ? 'Paid' : 'Unpaid';
        return {
          id: String(o.invoice.order_id),
          deliveryId: o.stopId ? `STOP-${o.stopId}` : '—',
          customer: o.customerName,
          invoiceNumber: o.invoice.invoice_number,
          amountDue: `$${Number(o.invoice.total_amount).toFixed(2)}`,
          paymentType: 'Cash' as PaymentType,
          status,
          invoiceId: o.invoice.id,
          orderId: o.invoice.order_id,
          stopId: o.stopId ?? null,
        };
      }),
    [outstanding, collectedOrderIds]
  );

  const selectedRow = rows.find((r) => r.orderId === selectedOrderId) ?? rows[0] ?? null;

  const recordDetails: RecordPaymentDetails = selectedRow
    ? {
        deliveryId: selectedRow.deliveryId,
        customer: selectedRow.customer,
        invoiceNumber: selectedRow.invoiceNumber,
        amountDue: selectedRow.amountDue,
      }
    : { deliveryId: '—', customer: '—', invoiceNumber: '—', amountDue: '$0.00' };

  const handleConfirm = async (input: { amount: number; paymentType: string; reference: string; notes: string; partial: boolean; amountTendered?: number }) => {
    if (!selectedRow) return;
    const method = input.paymentType.toLowerCase() === 'check'
      ? 'check'
      : input.paymentType.toLowerCase() === 'ach' || input.paymentType.toLowerCase() === 'card'
        ? 'mobile'
        : 'cash';
    // Collect by order — the backend finds or creates the invoice.
    await record({
      order_id: selectedRow.orderId,
      amount: input.amount,
      payment_method: method as any,
      reference_number: input.reference || undefined,
      notes: input.notes || undefined,
      delivery_stop_id: selectedRow.stopId ?? undefined,
      // Only meaningful for cash — the backend computes/validates change_due
      // server-side and rejects an insufficient tender.
      amount_tendered: method === 'cash' ? input.amountTendered : undefined,
    });
    await fetchMine();
  };

  return (
    <div className={styles.container}>
      <AsyncBoundary
        loading={loading && rows.length === 0}
        error={error}
        empty={!loading && rows.length === 0}
        emptyMessage="No outstanding collections on today's route"
        onRetry={() => { fetchMyStops(); fetchMine(); }}
      >
        <CollectionsTable rows={rows} onRecordPayment={(id) => setSelectedOrderId(Number(id))} />
        <RecordPaymentForm
          details={recordDetails}
          onConfirm={handleConfirm}
          onCancel={() => navigate('/dashboard')}
          submitting={saving}
        />
      </AsyncBoundary>
    </div>
  );
};

export const DriverCollectionsPage: React.FC = () => <DriverCollections />;

export { DriverCollections };
export default DriverCollections;
